import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Container,
  createTheme,
  ThemeProvider,
  TextField,
} from "@mui/material";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AxiosResponse } from "axios";
import http from "../../utils/http";
import { ArrowForwardRounded, KeyboardArrowDown } from "@mui/icons-material";
import "./landing.scss";
import isEmail from "validator/es/lib/isEmail";
// import { WaveSvg } from "./LandingPageIcons";
import EligibilityBenefits from "./EligibilityBenefits";
import { LANDING_BRAND } from "./landingBrand";
import FAQSection from "./FAQSection";
import StudyPurposeSection from "./StudyPurposeSection";
// import StudyAtAGlanceSection from "./StudyAtAGlanceSection";
// import VoluntaryParticipationSection from "./VoluntaryParticipationSection";
// import InterestedInParticipationSection from "./InterestedInParticipationSection";
import CustomLandingSections from "./CustomLandingSections";
import LandingPageHeader from "./LandingPageHeader";
import LandingRichText from "./LandingRichText";
import { resolveInitialLang, type StudyLanguage } from "./landingLanguage";
import i18n from "../../i18n";
import { useTranslation } from "react-i18next";

const landingPageTheme = createTheme({
  shape: {
    borderRadius: 6,
  },
  typography: {
    fontFamily: `"Inter", "Inter Fallback", sans-serif`,
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    body1: {
      fontWeight: 400,
      fontSize: 14,
      lineHeight: 1,
    },
    body2: {
      fontWeight: 400,
      fontSize: 12,
      lineHeight: 1,
    },
    subtitle1: {
      fontWeight: 400,
      fontSize: 16,
      lineHeight: 1,
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: 18,
      lineHeight: 1,
    },
    h3: {
      fontWeight: 600,
      fontSize: 18,
      lineHeight: "140%",
    },
    h2: {
      fontWeight: 700,
      fontSize: 30,
      lineHeight: 1,
    },
    h6: {
      fontWeight: 500,
      fontSize: 20,
      lineHeight: 1,
    },
  },
  palette: {
    primary: {
      main: LANDING_BRAND.primary,
    },
    secondary: {
      main: `${LANDING_BRAND.primary}1A`,
    },
    text: {
      primary: "#111827",
      secondary: "#4B5563",
    },
    divider: "#E0E3EB",
    background: {
      default: "#F4F5F7",
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          boxSizing: "border-box",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E0E3EB",
            borderRadius: "8px",
          },
          "& .MuiSvgIcon-root": {
            color: "#9CA3AF",
            fontSize: "20px",
          },
        },
        input: {
          height: "48px",
          boxSizing: "border-box",
          fontWeight: 500,
          fontSize: "14px",
          "&::placeholder": {
            fontWeight: 400,
            fontSize: "14px",
            color: "#9CA3AF",
            opacity: 1,
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        outlined: {
          padding: "12px 16px",
          height: "46px",
          display: "flex",
          alignItems: "center",
          boxSizing: "border-box",
        },
      },
      defaultProps: {
        IconComponent: KeyboardArrowDown,
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "capitalize",
          fontSize: "14px",
          height: "40px",
          padding: "16px 20px",
          fontWeight: 600,
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          fontSize: "14px",
          fontWeight: 500,
        },
        root: {
          "& .MuiInputBase-root": {
            height: "46px",
            "& input": {
              height: "100%",
            },
          },
        },
      },
    },
  },
});

const buildLandingPageData = ({
  content,
  images,
  studyType,
  studyName,
  heroImage,
}: {
  content?: Record<string, unknown>;
  images?: unknown[];
  studyType?: string;
  studyName?: string;
  heroImage?: { previewUrl?: string };
}) => ({
  ...(content || {}),
  images: images || [],
  studyType: studyType || "",
  studyName: studyName || "",
  ...(heroImage?.previewUrl
    ? { backgroundImageUrl: heroImage.previewUrl }
    : {}),
});

export const studyTypeOptions = [
  { label: "Market Insights Study", value: "market_insights_study" },
  { label: "Preference Study", value: "preference_study" },
  { label: "Expert Review", value: "expert_review" },
  { label: "Formative Usability Study", value: "formative_usability_study" },
  { label: "HF Pre-Validation Study", value: "hf_pre_validation_study" },
  { label: "HF Validation Study", value: "hf_validation_study" },
  { label: "Observational Study", value: "observational_study" },
  { label: "Interventional Study", value: "interventional_study" },
  { label: "Patient Registry", value: "patient_registry" },
];

const LandingPage = () => {
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [lang, setLang] = useState("en");
  const [studyLangs, setStudyLangs] = useState<StudyLanguage[]>([]);
  const { studySlug } = useParams();

  const loadLandingContent = useCallback(
    async (selectedLang: string) => {
      const url = `/study/configuration-by-slug?studySlug=${studySlug}&pageKey=landing_page&lang=${selectedLang}`;
      const res: AxiosResponse = await http.get(url);
      const config = res?.data?.data;
      const title = config.name;
      const pages = config?.pages || [];
      const studyType = studyTypeOptions.find(
        (option) => option.value === config.type,
      );
      let content = pages?.find((page: any) => page.lang === selectedLang)
        ?.content;
      if (!content && selectedLang !== "en") {
        content = pages?.find((page: any) => page.lang === "en")?.content;
      }
      let images = pages?.find((page: any) => page.lang === selectedLang)
        ?.images;
      if (!images && selectedLang !== "en") {
        images = pages?.find((page: any) => page.lang === "en")?.images;
      }

      const heroImage = images?.find((img: any) => img.key === "hero");

      setData(
        buildLandingPageData({
          content,
          images,
          studyType: studyType?.label,
          studyName: title,
          heroImage,
        }),
      );
    },
    [studySlug],
  );

  const applyLanguage = useCallback(
    async (selectedLang: string, options?: { showPageLoader?: boolean }) => {
      localStorage.setItem("lang", selectedLang);
      setLang(selectedLang);
      i18n.changeLanguage(selectedLang);

      if (!studySlug) return;

      const showPageLoader = options?.showPageLoader ?? false;
      try {
        if (showPageLoader) {
          setContentLoading(true);
        }
        await loadLandingContent(selectedLang);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        if (showPageLoader) {
          setContentLoading(false);
        }
      }
    },
    [loadLandingContent, studySlug],
  );

  const handleLanguageChange = useCallback(
    (selectedLang: string) => {
      if (selectedLang !== lang) {
        applyLanguage(selectedLang, { showPageLoader: true });
      }
    },
    [applyLanguage, lang],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const langRes: AxiosResponse = await http.get(
          `/data/study-languages?studySlug=${studySlug}`,
        );

        const langs: StudyLanguage[] = langRes?.data?.data || [];
        setStudyLangs(langs);

        const storedLang = localStorage.getItem("lang");
        const initialLang = resolveInitialLang(langs, storedLang);

        await applyLanguage(initialLang);
      } catch (err) {
        setLang("en");
        i18n.changeLanguage("en");
        setData(buildLandingPageData({ images: [] }));
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };
    if (studySlug) {
      fetchData();
    } else {
      setLang("en");
      i18n.changeLanguage("en");
      setData(buildLandingPageData({ images: [] }));
      setLoading(false);
    }
  }, [studySlug, applyLanguage]);

  return (
    <ThemeProvider theme={landingPageTheme}>
      {!loading ? (
        <Box sx={{ height: "100vh", width: "100vw", overflow: "auto", position: "relative" }}>
          {contentLoading && (
            <Box
              sx={{
                position: "fixed",
                inset: 0,
                zIndex: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.5)",
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <LandingPageHeader
            languages={studyLangs}
            lang={lang}
            onLanguageChange={handleLanguageChange}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: "calc(100vh - 4rem)",
            }}
          >
            <Box
              sx={{
                position: "relative",
                color: "#111827",
                ...(data?.backgroundImageUrl
                  ? {
                    backgroundImage: `url('${data.backgroundImageUrl}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center top",
                  }
                  : {
                    backgroundImage: `linear-gradient(to bottom, ${LANDING_BRAND.cream} 0%, ${LANDING_BRAND.peach} 50%, ${LANDING_BRAND.primary} 100%)`,
                  }),
                minHeight: "calc(100vh - 4rem)",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                pt: { xs: 8, md: 12 },
                pb: { xs: 6, md: 8 },
                overflow: "hidden",
              }}
            >
              <Container
                sx={{
                  position: "relative",
                  zIndex: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  maxWidth: {
                    xs: "100%",
                    md: "60%",
                  },
                }}
              >
                {/* <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2, // px-4 equivalent (1 unit = 8px)
                    py: 0.5, // py-1 equivalent
                    mb: 3, // mb-6 equivalent
                    borderRadius: "999px", // fully rounded
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight="regular"
                    sx={{
                      fontSize: "0.875rem",
                      lineHeight: "1.25rem",
                    }}
                  >
                    Formative Usability Study
                  </Typography>
                </Box> */}
                {data?.hero?.title?.trim() && (
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      mb: 2,
                      fontSize: { xs: "40px", md: "60px" },
                    }}
                  >
                    {data.hero.title}
                  </Typography>
                )}
                {data?.hero?.subtitle?.trim() && (
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      lineHeight: "2rem",
                      fontWeight: "regular",
                      mb: "0.5rem",
                      color: "#374151",
                    }}
                  >
                    {data.hero.subtitle}
                  </Typography>
                )}
                <LandingRichText
                  html={data?.hero?.description}
                  sx={{
                    mb: data?.hero?.description ? "2.5rem" : 0,
                    fontSize: "1.25rem",
                    lineHeight: "1.75rem",
                    color: "#4B5563",
                    "& p": { margin: "0 0 0.75rem", color: "inherit" },
                    "& p:last-child": { marginBottom: 0 },
                    "& ul, & ol": {
                      margin: "0.5rem 0",
                      paddingLeft: "1.5rem",
                    },
                    "& a": { color: LANDING_BRAND.primaryDark },
                    "& strong": { color: "inherit" },
                  }}
                />
                <ScreeningContainer />
              </Container>
              {/* Hero image includes wave shapes — SVG divider removed */}
              {/* <Box
                sx={{
                  position: "absolute",
                  bottom: -1,
                  left: 0,
                  right: 0,
                  width: "100%",
                  lineHeight: 0,
                  zIndex: 1,
                }}
              >
                <WaveSvg />
              </Box> */}
            </Box>

            <StudyPurposeSection data={data?.studyPurpose} />

            {/* <StudyAtAGlanceSection /> */}

            <EligibilityBenefits
              header={data?.eligibilityBenefitsHeader}
              eligibilityItems={data?.eligibility}
              benefitItems={data?.benefits}
            />
            {/* <VoluntaryParticipationSection /> */}
            {/* <InterestedInParticipationSection /> */}
            <CustomLandingSections sections={data?.sections} />
            <FAQSection header={data?.faqHeader} items={data?.faq} />
            <LandingPageFooter />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </ThemeProvider>
  );
};

const LandingPageFooter = () => {
  const { t } = useTranslation("landing-page");

  return (
    <Box
      component="footer"
      sx={{
        px: 3,
        py: 4,
        textAlign: "center",
        borderTop: "1px solid #E0E3EB",
        backgroundColor: "#fff",
      }}
    >
      <Typography variant="body1" sx={{ color: "#4B5563" }}>
        {t("FOOTER.READ_OUR")}
        <Link
          to="/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: LANDING_BRAND.primaryDark, fontWeight: 600 }}
        >
          {t("FOOTER.PRIVACY_POLICY")}
        </Link>
        .
      </Typography>
    </Box>
  );
};

const ScreeningContainer = () => {
  const { t, i18n: i18nInstance } = useTranslation("landing-page");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { studySlug } = useParams();
  const lang = i18nInstance.language;

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handleStartScreening = async () => {
    if (isEmail(email)) {
      try {
        setLoading(true);
        const body: any = {
          email,
          studySlug,
        };

        if (lang && lang !== "en") {
          body.lang = lang;
        }

        await http.post("/participant/auth/magic-link/generate", body);
        navigate(
          `/study/${studySlug}/check-email?email=${encodeURIComponent(email)}`,
        );
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    } else {
      toastMessage("warning", t("SCREENING.INVALID_EMAIL"));
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleStartScreening();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: 2,
        width: "100%",
      }}
    >
      <TextField
        placeholder={t("SCREENING.EMAIL_PLACEHOLDER")}
        id="email"
        sx={{
          "& .MuiInputBase-root": {
            height: 48,
            backgroundColor: "#fff",
          },
          "& .MuiOutlinedInput-root": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#D1D5DB",
            },
            "&.Mui-focused": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#D1D5DB",
              },
            },
            "&:hover:not(.Mui-focused)": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#D1D5DB",
              },
            },
          },
          maxWidth: "350px",
          width: "100%",
        }}
        value={email}
        onChange={handleEmailChange}
        onKeyDown={handleKeyDown}
      />

      <Button
        variant="contained"
        size="large"
        endIcon={
          loading ? (
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          ) : (
            <ArrowForwardRounded
              sx={{
                color: "#fff",
                fontSize: "24px",
              }}
            />
          )
        }
        sx={{
          height: "48px",
          backgroundColor: LANDING_BRAND.primary,
          color: "#fff",
          border: "none",
          borderRadius: "8px",
          fontSize: "1.125rem",
          fontWeight: 500,
          textTransform: "none",
          boxShadow: "0 4px 14px rgba(232, 69, 122, 0.35)",
          px: "24px",
          "&:hover": {
            backgroundColor: LANDING_BRAND.primaryDark,
            boxShadow: "0 6px 20px rgba(232, 69, 122, 0.4)",
          },
        }}
        onClick={handleStartScreening}
      >
        {t("SCREENING.START_BUTTON")}
      </Button>
    </Box>
  );
};

export default LandingPage;
